import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ResponsiveBar } from '@nivo/bar';
import { Row, Col, Card, CardBody, CardTitle } from 'reactstrap';
import { loadAccountingStats } from '../../redux';
import { Utils } from '../../services';
import { Dropdown } from 'semantic-ui-react';
import theme from './theme';
import _ from 'lodash';

const AccountingBarChart = ({
    filters = {},
    icon = 'fa-chart-line',
    currency = 'chf',
    colorScheme = 'nivo',
    onClickOnNode,
    cashregisters
}) => {
    const HASH = `trend`;

    const { t, i18n } = useTranslation();
    const dispatch    = useDispatch();

    const money  = amount => Utils.formatMoney(amount, i18n.language, currency);
    const number = number => Utils.formatNumber(number, i18n.language);

    const [groupBy, setGroupBy]     = useState('day')
    const [compareTo, setCompareTo] = useState(null)

    const stats             = useSelector(state => state.accountingStats.stats)[HASH];
    const { KUserSettings } = useSelector(state => state.userSettings);

    useEffect(() => {
        dispatch(loadAccountingStats(HASH, {
            ...filters,
            group_by: groupBy,
            compare_to: compareTo
        }));
    }, [dispatch, filters, HASH, groupBy, compareTo]);

    const keys = [
        t(`accounting.filters.compare_to_${compareTo?.replace('-', '_')}`),
        t('accounting.filters.compared')
    ];

    const data = React.useMemo(() => {
        let data = stats?.data?.current?.results?.map(result => {
            let x   = result[groupBy];
            if (groupBy === 'day_of_week')
                x = t(`common.${Utils.days(x - 1)?.full_label}`);
            else if (groupBy === 'type')
                x = t(`accounting.types.${x}`)?.substring(0, 20);
            else if (groupBy === 'payment_method')
                x = t(`payment_methods.${x}`)?.substring(0, 20);
            else if (groupBy === 'tag') {
                x   = x?.name;
            } else if (groupBy === 'cashregister')
                x = Utils.localized_or_fallback(
                    cashregisters?.find(c => c._id === x)?.name,
                    i18n.language
                );
            else if (_.isObject(x))
                x = Utils.localized_or_fallback(x, i18n.language);
            return {
                x,
                key: result[groupBy],
                [keys[1]]: result.total_amount,
                count: parseInt(result.count, 10)
            };
        });
        if (stats?.data?.comparison?.results?.length) {
            data = data.map(result => {
                return {
                    ...result,
                    [keys[0]]: stats?.data?.comparison?.results?.find(r => {
                        if (groupBy === 'tag')
                            return r.tag?._id === result.key?._id;
                        return r[groupBy] === result.key;
                    })?.total_amount ?? 0
                };
            });
        }

        // remove falsy values
        data = data?.filter(d => d[keys[1]] !== 0 || d[keys[0]]);

        return data;
    }, [stats, groupBy]);

    const totalCount = useMemo(() => (
        data?.reduce((sum, item) => sum + item.count, 0)
    ), [stats]);
    const totalValue = useMemo(() => (
        data?.reduce((sum, item) => sum + item[keys[1]], 0)
    ), [stats]);

    const title = t(`accounting.stats.${HASH}.title`);

    const onClick = (node, event) => {
        onClickOnNode(node.data._id);
    };

    const groupByOptions = [
        'type',
        'tag', 'tags',
        'cashregister',
        'author',
        'payment_method',
        'pricing',
        'year',
        'month', 'month_of_year',
        'week', 'week_of_year',
        'day', 'day_of_year', 'day_of_month', 'day_of_week',
        'hour_of_day',
        'half_hour_of_day',
        'quarter_hour_of_day'
    ].map(property => ({
        key: property,
        text: t(`accounting.filters.group_by_${property}`),
        value: property
    }));

    const isTimingGroupBy = React.useMemo(() => ([
        'year', 'month', 'month_of_year',
        'week', 'week_of_year',
        'day', 'day_of_week', 'day_of_month', 'day_of_year',
        'hour', 'hour_of_day',
        'half_hour', 'half_hour_of_day',
        'quarter_hour', 'quarter_hour_of_day'
    ].includes(groupBy)), [groupBy]);

    const compareToOptions = React.useMemo(() => {
        const options = [
            {key: 'last-year',  text: t('accounting.filters.compare_to_last_year'), value: 'last-year'}
        ];

        if (!isTimingGroupBy || (groupBy !== 'year' && groupBy !== 'month' && groupBy !== 'month_of_year' && groupBy !== 'week' && groupBy !== 'week_of_year' && groupBy !== 'day_of_year')) {
            options.push({key: 'last-month', text: t('accounting.filters.compare_to_last_month'), value: 'last-month'});
        }

        if (!isTimingGroupBy || [ 'hour_of_day', 'half_hour_of_day', 'quarter_hour_of_day' ].includes(groupBy))
            options.push({key: 'last-day',  text: t('accounting.filters.compare_to_last_day'), value: 'last-day'});

        return options;
    }, [groupBy]);

    const xAxisLegend = t(`accounting.filters.group_by_${groupBy}`);
    const yAxisLegend = t('accounting.stats.currency_amount', {currency: Utils.currency()});

    const formatLabel = d => {
        return number(d.value);
    }

    return (
        <Card className="mb-4">
            <CardBody>
                <CardTitle>
                    <i className={`fa ${icon}`}></i> {title}
                </CardTitle>

                <Row>
                    <Col xs={12} sm={6}>
                        <label>{t('accounting.filters.group_by')}</label>
                        <div>
                            <Dropdown
                                options={groupByOptions}
                                placeholder={t('accounting.filters.select_grouping_options')}
                                defaultValue={groupBy}
                                onChange={(e, data) => setGroupBy(data.value)}
                                className="w-100"
                                search
                                selection
                            />
                        </div>
                    </Col>
                    <Col xs={12} sm={6}>
                        <label>{t('accounting.filters.compare_to')}</label>
                        <div>
                            <Dropdown
                                options={compareToOptions}
                                defaultValue={compareTo}
                                onChange={(e, data) => setCompareTo(data.value)}
                                className="w-100"
                                placeholder={t('accounting.filters.no_comparison')}
                                search
                                selection
                                clearable
                            />
                        </div>
                    </Col>
                </Row>

                {stats?.isLoading ? (
                    <div className="text-center py-5">
                        <i className="fa fa-spinner fa-spin fa-2x"></i>
                        <p className="mt-2">{t('accounting.stats.loading_data')}</p>
                    </div>
                ) : (stats?.error ? (
                    <div className="text-center py-5 text-danger">
                        <i className="fa fa-exclamation-triangle fa-2x"></i>
                        <p className="mt-2">{stats?.error}</p>
                    </div>
                ) : (!data?.length ? (
                    <div className="text-center py-5">
                        <p>{t('accounting.stats.no_data')}</p>
                    </div>
                ) : (
                    <>
                        <div className="mb-3">
                            <p className="mb-1"><strong>{t('accounting.stats.nb_of_transactions')}:</strong> {number(totalCount)}</p>
                            <p className="mb-1"><strong>{t('accounting.stats.total_amount')}:</strong> {money(totalValue)}</p>
                        </div>
                        <div style={{height: 400}}>
                            <ResponsiveBar
                                data={data}
                                indexBy="x"
                                keys={keys}
                                groupMode="grouped"
                                labelSkipWidth={12}
                                labelSkipHeight={12}
                                label={ formatLabel }
                                borderRadius={4}
                                colors={{scheme: colorScheme}}
                                theme={theme[KUserSettings.darkTheme ? 'dark' : 'light']}
                                legends={[
                                    {
                                        dataFrom: 'keys',
                                        anchor: 'bottom',
                                        direction: 'row',
                                        translateY: 100,
                                        itemsSpacing: 3,
                                        itemWidth: 160,
                                        itemHeight: 16
                                    }
                                ]}
                                axisBottom={{
                                    legend: xAxisLegend,
                                    legendOffset: 50,
                                    tickRotation: -45,
                                    truncateTickAt: 20
                                }}
                                axisLeft={{ legend: yAxisLegend, legendOffset: 10 }}
                                margin={{
                                    top: 50,
                                    right: 50,
                                    bottom: 100,
                                    left: 60
                                }}
                                tooltip={({ label, value }) => (
                                    <div
                                        style={{
                                            background: '#FFFFFF',
                                            color: '#000000',
                                            padding: '9px 12px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        <div><strong>{label}</strong></div>
                                        <div>{t('Montant')}: {money(value)}</div>
                                        <div>{t('Pourcentage')}: {(value / totalValue * 100).toFixed(1)}%</div>
                                    </div>
                                )}
                            />
                        </div>
                    </>
                )))}
            </CardBody>
        </Card>
    );
};

export default AccountingBarChart;
